import { memo, useEffect, useState } from 'react';
import { Badge, Switch } from '@fluentui/react-components';
import { BeakerRegular } from '@fluentui/react-icons';
import {
  featureFlagConfig,
  getFeatureFlagOverrides,
  isExperimentalFlagVisible,
  isFeatureFlagForcedByUrl,
  setFeatureFlagOverride,
  subscribeToFeatureFlagChanges,
  type FeatureFlagName,
} from '@/featureFlags';
import { useSettingCardStyles } from './sectionStyles';

// Only flags not marked `hidden` are advertised in the lab. Hidden flags
// stay in the registry so existing call sites and `?features=` deep-links
// keep resolving, but they don't show up here.
const FLAG_NAMES = (Object.keys(featureFlagConfig) as FeatureFlagName[]).filter(
  isExperimentalFlagVisible,
);

/**
 * Experimental feature flags. URL `?features=` still takes precedence
 * over user toggles — when a flag is URL-forced, the switch is disabled
 * and a "URL override" badge appears.
 */
export const ExperimentalSection = memo(() => {
  const styles = useSettingCardStyles();
  // Re-render trigger driven by the cross-tab event. `setFeatureFlagOverride`
  // dispatches `otto:feature-flags-changed`, which the effect below listens to,
  // so we don't need an extra manual bump after each toggle.
  const [, setTick] = useState(0);

  useEffect(() => {
    return subscribeToFeatureFlagChanges(() => setTick((t) => t + 1));
  }, []);

  // Fresh copy on every render; the parsed override map itself is cached in
  // featureFlags.ts and invalidated on same-tab and cross-tab writes.
  const overrides = getFeatureFlagOverrides();

  return (
    <>
      <div className={styles.banner} role="note">
        <BeakerRegular />
        <span>
          In-development features. Behavior may change or break. URL flags{' '}
          <span className={styles.mono}>?features=…</span> still take precedence over these toggles.
        </span>
      </div>

      <div className={styles.list}>
        {FLAG_NAMES.map((name) => {
          const def = featureFlagConfig[name];
          const forcedByUrl = isFeatureFlagForcedByUrl(name);
          const override = overrides[name];
          const switchChecked = forcedByUrl ? true : (override ?? def.defaultEnabled);

          const handleToggle = (next: boolean) => {
            // Clear the override when the user toggles back to the default
            // instead of pinning the default explicitly — keeps localStorage
            // tidy and lets a later config change in code propagate to this
            // user without their stored value pinning the old default.
            setFeatureFlagOverride(name, next === def.defaultEnabled ? undefined : next);
          };

          return (
            <div key={name} className={styles.listRow}>
              <div>
                <p className={styles.cardLabel}>
                  {def.label}
                  {forcedByUrl && (
                    <Badge appearance="tint" color="warning" size="small" className={styles.badgeOffset}>
                      URL override
                    </Badge>
                  )}
                  {!forcedByUrl && override !== undefined && (
                    <Badge appearance="tint" color="brand" size="small" className={styles.badgeOffset}>
                      Custom
                    </Badge>
                  )}
                </p>
                <p className={styles.cardHelp}>
                  {def.description}
                </p>
                {/*
                 * Quiet identifier for the flag name. We deliberately do not
                 * show "Currently: On/Off" here — the Switch itself already
                 * communicates that state, and the duplicate text added
                 * visual noise when scrolling multiple flags. The flag name
                 * remains because it's the canonical handle for ?features=
                 * deep-links and bug reports.
                 */}
                <p className={`${styles.cardHelp} ${styles.helpRowMargin}`}>
                  <span className={styles.mono}>{name}</span>
                </p>
              </div>
              <div className={styles.cardControl}>
                <Switch
                  checked={switchChecked}
                  disabled={forcedByUrl}
                  onChange={(_, data) => handleToggle(data.checked)}
                  aria-label={`Toggle ${def.label}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
});
ExperimentalSection.displayName = 'ExperimentalSection';
